/**
 * This file is a part of KingLiving's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import PropTypes from 'prop-types';

import {
  Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Text,
  useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ChevronDownIcon} from '@amblique/base/app/components/icons';
import {Markup} from '@amblique/base/app/components/shared/markup';
import {useIsLargeScreen} from '@amblique/king-living/app/hooks/use-memoized-breakpoint';

/**
 * @param {{content: string|string[]|object|object[]}} args
 * @param {string} stylesName
 *
 * @returns {React.ReactElement}
 */
export function ContentAccordion({content, ...props}) {
  let children;

  if (content) {
    if (Array.isArray(content)) {
      const isMobile = !useIsLargeScreen();
      const styles = useMultiStyleConfig('Footer');

      const accordionContents = content.map((item, index) => {
        const uniqueKey = `accordion-item-${index}`;

        return isMobile ? (
          <Accordion
            key={`${uniqueKey}-accordion`}
            variant="plain"
            allowToggle
            {...styles.accordionSection}
          >
            <AccordionItem
              key={`${uniqueKey}-item`}
              {...styles.accordionItem}
            >
              <AccordionButton
                key={`${uniqueKey}-button`}
                {...styles.accordionButton}
              >
                <Text as="h3" {...styles.accordionHeading}>
                  {item.name}
                </Text>
                <ChevronDownIcon {...styles.accordionIcon} />
              </AccordionButton>
              <AccordionPanel {...styles.accordionPanel}>
                <Markup
                  {...styles.listItemContent}
                  display="flex"
                  flexDirection="column"
                  source={item.body}
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        ) : (
          <Box
            key={`${uniqueKey}-box`}
            {...styles.listItem}
          >
            <Text as="h3" {...styles.listItemTitle}>
              {item.name}
            </Text>
            <Markup
              {...styles.listItemContent}
              display="flex"
              flexDirection="column"
              source={item.body}
            />
          </Box>
        );
      });

      children = (
        <VStack {...styles.accordionWrapper}>
          {accordionContents}
        </VStack>
      );
    } else {
      children = content && <Markup source={content} {...props} />;
    }
  }

  return children;
}

ContentAccordion.displayName = 'ContentAccordion';
ContentAccordion.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
  ]).isRequired,
};
